import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import Button from "../Button/Button";
import { ButtonTextSizeEnum, ButtonVariantEnum } from "../Button/button.types";
import classes from "./adminPanelForm.module.scss";
import { AdminServices } from "../../services/admin/AdminServices";
import { FontWeightEnum, Text, TextVariantsEnum } from "../Text";
import styles from "../../pages/adminPanel/CampaignsList/campaignsList.module.scss";
import { useLoginActions } from "../../store/login/useLoginActions";
import { useLoginSelector } from "../../store/login/useLoginSelectors";
import { toast, Toaster } from "react-hot-toast";
import { validateEmail } from "./validationScreen";
import SelectReact from "react-select";
import { Spinner } from "../Spinner/Spinner.component";

// Time and resolution options
const timeOptions = Array.from({ length: 24 }, (_, i) => {
  const hour = i % 12 === 0 ? 12 : i % 12;
  const suffix: any = i < 12 ? "AM" : "PM";
  return {
    label: `${hour.toString().padStart(2, "0")}:00 ${suffix}`,
    value: `${hour.toString().padStart(2, "0")}:00 ${suffix}`,
  };
});

const resolutionOptions = [
  { label: "720 PX", value: "P720" },
  { label: "1080 PX", value: "P1080" },
  { label: "2K", value: "K2" },
  { label: "4K", value: "K4" },
];

const orientationOptions = [
  { label: "Landscape", value: "LANDSCAPE" },
  { label: "Portrait", value: "PORTRAIT" },
];

const isPublicOptions = [
  { label: "True", value: true },
  { label: "False", value: false },
];

const typeOptions = [
  { label: "Screen", value: "TV" },
  { label: "Channel", value: "CHANNEL" },
];

const customStyles = {
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#f3f4f6" : "white",
    color: "black",
  }),
  control: (provided: any) => ({
    ...provided,
    padding: "0.6rem", // 1rem = 16px
    borderRadius: "0.75rem", // rounded-xl in Tailwind
    borderColor: "#d1d5db",
    boxShadow: "none",
    borderWidth: "1px", // optional to mimic Tailwind border
  }),
};

const ScreenForm = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const { userDataById } = useLoginSelector();
  const [userData, setUserData] = useState(null);
  const { actionGetUserById, actionClearUserDataById } = useLoginActions();

  const [screenName, setScreenName] = useState("");
  const [storeName, setStoreName] = useState("");
  const [organisationName, setOrganisationName] = useState("");
  const [gender, setGender] = useState("");
  const [merchantAdQuota, setMerchantAdQuota] = useState<number | "">("");
  const [tags, setTags] = useState<string[]>([]);
  const [resolution, setResolution] = useState<any>(null);
  const [orientation, setOrientation] = useState<any>(null);
  const [monthlyFootfall, setMonthlyFootfall] = useState<number | "">("");
  const [startTime, setStartTime] = useState<any>(null);
  const [endTime, setEndTime] = useState<any>(null);
  const [isPublic, setIsPublic] = useState({ label: "True", value: true });
  const [type, setType] = useState<any>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isModel, setIsModel] = useState(false);
  const [screenId, setScreenId] = useState("");
  const [IsSearched, setIsSearched] = useState(false);

  useEffect(() => {
    return () => {
      actionClearUserDataById();
    };
  }, []);

  const handleSearch = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsLoading(true);
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    actionGetUserById({ email });
    setIsLoading(false);
  };

  const handleTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tagInput = e.target.value;
    const tagList = tagInput
      .split(",")
      .map((tag: any) => tag.trim())
      .filter((tag: any) => tag !== "");
    setTags(tagList);
  };

  const resetFormFields = () => {
    setScreenName("");
    setStoreName("");
    setOrganisationName("");
    setGender("");
    setMerchantAdQuota("");
    setTags([]);
    setResolution(null);
    setOrientation(null);
    setMonthlyFootfall("");
    setStartTime(null);
    setEndTime(null);
    setEmail("");
    setType(null);
  };

  //populate the fields
  useEffect(() => {
    if (userDataById.status === "failed") {
      toast.error("User Does not exist");
    }

    if (userDataById.status === "success") {
      toast.success("User Found Successfully");
      setIsSearched(true);

      const data = userDataById.data;

      // Populate backend values
      setStoreName(data?.storeName || "");
      setOrganisationName(data?.organization.name || "");
      setGender(data?.gender || "");
      setMonthlyFootfall(data?.avgMonthlyFootfall || "");
    }
  }, [userDataById]);

  const handleRegister = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (
      !screenName ||
      !merchantAdQuota ||
      !resolution ||
      !orientation ||
      !startTime ||
      !endTime || 
      !isPublic ||
      !type
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    const convertTimeToMinutes = (timeStr: string): number => {
      const [time, modifier] = timeStr.split(" ");
      let [hours, minutes] = time.split(":").map(Number);

      if (modifier === "PM" && hours !== 12) hours += 12;
      if (modifier === "AM" && hours === 12) hours = 0;

      return hours * 60 + minutes;
    };

    const startMinutes = convertTimeToMinutes(startTime.value);
    const endMinutes = convertTimeToMinutes(endTime.value);

    const userData = userDataById.data;

    const apiData = {
      name: screenName || "Unnamed Screen",
      orgId: userData.organization.orgId || "",
      type: type?.value || "TV",
      userId: userData.userId || "",
      status: userData.status || "",
      address: userData.address || {
        line1: "",
        line2: "",
        city: "",
        state: "",
        country: "",
        zip: "",
        locality: "",
        lat: "",
        lng: "",
      },
      tags: tags || [], // Still included in payload, but not mandatory
      resolution: resolution?.value || "",
      orientation: orientation?.value || "",
      storeName: userData.storeName || "",
      merchantAdsQuota: 50,
      isPublic: isPublic?.value || false,
      category: userData.category || "",
      subCategory: userData.subCategory || "",
      socialIndicator: userData.socialIndicator || "",
      openingHourInMinutes: userData.openingHourInMinutes || 0,
      closingHourInMinutes: userData.closingHourInMinutes || 0,
      persona: userData.persona || [],
      incomeCategories: userData.incomeCategories || [],
      gender: "MALE",
      avgMonthlyFootfall: 1000,
      ageGroups: userData.ageGroups,
      notAllowedCategories: userData.notAllowedCategories || [],
      notAllowedSubcategories: userData.notAllowedSubcategories || [],
    };

    try {
      setIsLoading(true);
      // console.log("Submitting payload:", apiData);

      const response = await AdminServices.registerScreen(apiData);

      // console.log("API response:", response);

      if (response.response.status == "SUCCESS") {
        toast.success("Screen registered successfully.");
        setIsModel(true);
        setScreenId(response.response.screenId);
        setIsSearched(false);
        resetFormFields();
        setEmail("");
      } else {
        toast.error(response?.errorMessage || "Screen registration failed.");
      }
    } catch (error: any) {
      console.error("Error registering screen:", error);
      toast.error(
        error?.response?.data?.errorMessage || "Failed to register screen."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!IsSearched) {
      resetFormFields();
    }
  }, [IsSearched]);

  return (
    <div className="flex flex-col gap-5 screen-container">
      <Toaster />
      <div className="flex gap-5 input-search-box">
        <input
          type="email"
          value={email}
          placeholder="User ID or Email"
          onChange={(e) => setEmail(e.target.value)}
          className="w-1/2 p-4 border rounded-xl border-neutral-300"
        />
        <button
          onClick={handleSearch}
          className="input-searchbtn bg-[#6938EF] rounded-xl px-5 py-3 text-white text-lg"
        >
          Search
        </button>
        {error && <p style={{ color: "red", marginTop: "5px" }}>{error}</p>}
      </div>

      <div className="bg-white  rounded-xl screen-form-container min-h-[30vh] flex flex-col">
        <div className="pl-6 pt-4 text-[2.5rem] font-bold heading">
          Screen Info{" "}
        </div>

        {IsSearched ? (
          <>
            {isLoading ? (
              <div className="flex items-center justify-center">
                <CircularProgress />
              </div>
            ) : (
              <>
                <form action="" className={`p-6 space-y-4  `}>
                  <div className="grid grid-cols-2 gap-10">
                    <div>
                      <label className="block mb-1 text-lg ">
                        Screen Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={screenName}
                        onChange={(e) => setScreenName(e.target.value)}
                        className="w-full p-4 border rounded-xl border-neutral-300"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-lg font-medium">
                        Store Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={storeName}
                        disabled
                        className="w-full p-4 border cursor-not-allowed rounded-xl border-neutral-300"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-lg font-medium">
                        Organisation Name{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={organisationName}
                        disabled
                        className="w-full p-4 border cursor-not-allowed rounded-xl border-neutral-300"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-lg font-medium">
                        Gender <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={gender}
                        disabled
                        className="w-full p-4 border cursor-not-allowed rounded-xl border-neutral-300"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-lg font-medium">
                        Merchant Ad Quota{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={merchantAdQuota}
                        onChange={(e) =>
                          setMerchantAdQuota(Number(e.target.value))
                        }
                        className="w-full p-4 border rounded-xl border-neutral-300 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-lg font-medium">
                        Tags (comma separated)
                      </label>
                      <input
                        type="text"
                        onChange={handleTagChange}
                        className="w-full p-4 border rounded-xl border-neutral-300"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-lgfont-medium">
                        Resolution <span className="text-red-500">*</span>
                      </label>
                      <SelectReact
                        styles={customStyles}
                        options={resolutionOptions}
                        value={resolution}
                        onChange={setResolution}
                        placeholder="Select Resolution"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-lgfont-medium">
                        Orientation <span className="text-red-500">*</span>
                      </label>
                      <SelectReact
                        styles={customStyles}
                        options={orientationOptions}
                        value={orientation}
                        onChange={setOrientation}
                        placeholder="Select Resolution"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-lg font-medium">
                        Avg Monthly Footfall{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={monthlyFootfall}
                        disabled
                        className="w-full p-4 border cursor-not-allowed rounded-xl border-neutral-300"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-lg font-medium">
                        Start Time <span className="text-red-500">*</span>
                      </label>
                      <SelectReact
                        styles={customStyles}
                        options={timeOptions}
                        value={startTime}
                        onChange={setStartTime}
                        placeholder="Select Start Time"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 text-lg font-medium">
                        End Time <span className="text-red-500">*</span>
                      </label>
                      <SelectReact
                        styles={customStyles}
                        options={timeOptions}
                        value={endTime}
                        onChange={setEndTime}
                        placeholder="Select End Time"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-lg font-medium">
                        Public <span className="text-red-500">*</span>
                      </label>
                      <SelectReact
                        styles={customStyles}
                        options={isPublicOptions}
                        value={isPublic}
                        onChange={(newValue) => {
                          if (newValue) setIsPublic(newValue);
                        }}
                        placeholder="Select Is Public"
                      />
                    </div>

                    <div>
                      <div>
                        <label className="block mb-1 text-lg font-medium">
                          Type <span className="text-red-500">*</span>
                        </label>
                        <SelectReact
                          styles={customStyles}
                          options={typeOptions}
                          value={type}
                          onChange={setType}
                          placeholder="Select Type"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-5 buttons ">
                    <button className="px-5 py-3 text-lg border border-neutral-300 cancelbtn rounded-xl">
                      Cancel
                    </button>
                    <button
                      className="registerbtn bg-[#6938EF] rounded-xl px-5 py-3 text-white text-lg"
                      onClick={handleRegister}
                    >
                      Register
                    </button>
                  </div>
                </form>
              </>
            )}
          </>
        ) : (
          <>
            {isLoading ? (
              <div className="flex items-center justify-center">
                <CircularProgress />
              </div>
            ) : (
              <div className="p-4 text-2xl text-gray-600 h-[20vh]  flex justify-center items-center">
                Please enter a user email above and click{" "}
                <span className="font-semibold">"Search"</span> to begin screen
                registration.
              </div>
            )}
          </>
        )}
      </div>
      {isModel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-xl p-6 shadow-lg w-[90%] max-w-md text-center">
            <h2 className="mb-4 text-2xl font-bold text-green-600">
              Screen registered successfully
            </h2>
            <p className="mb-2">Screen ID:</p>
            <div className="flex items-center justify-between px-4 py-2 mb-4 border rounded-lg">
              <span className="truncate">{screenId}</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(screenId);
                  toast.success("Copied to clipboard");
                }}
                className="ml-4 text-sm text-blue-600 hover:underline"
              >
                Copy
              </button>
            </div>
            <button
              onClick={() => setIsModel(false)}
              className="bg-[#6938EF] text-white px-5 py-2 rounded-xl mt-2"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenForm;
